import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { AppError } from '../api/errors';
import type {
  AutonomyKind,
  DecisionKind,
  Employee,
  EmployeeProfile,
  EmployeeRequest,
  EmployeeRequestInput,
  Item,
  Page,
} from '../domain/models';
import { isWorking } from '../domain/models';
import * as repo from './repository';

// Server state for the screens. Lists refresh themselves every 20 s and when the tab comes back, as
// the mobile app does on resume; every decision, handoff or result refreshes everything it touches.

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      // A dropped connection is worth two more tries; a 4xx answer is not.
      retry: (count, e) => count < 2 && e instanceof AppError && e.kind === 'network',
    },
  },
});

export const keys = {
  workspaces: ['workspaces'] as const,
  employees: ['employees'] as const,
  employee: (id: string) => ['employees', id] as const,
  inbox: (f: repo.ItemFilter) => ['items', 'inbox', f] as const,
  items: (f: repo.ItemFilter) => ['items', 'all', f] as const,
  item: (id: string) => ['item', id] as const,
  events: (id: string) => ['item', id, 'events'] as const,
  related: (id: string) => ['items', 'related', id] as const,
  history: (workspaceId?: string) => ['history', workspaceId ?? null] as const,
  catalog: ['catalog'] as const,
  employeeRequests: ['employee-requests'] as const,
};

const refreshEvery = 20_000;
/** How often to check on an employee working on something. */
const pollEvery = 15_000;

const nextCursor = (p: Page<unknown>) => p.nextCursor;
export const flatten = <T>(d: InfiniteData<Page<T>> | undefined) => d?.pages.flatMap((p) => p.items);

export const useWorkspaces = () => useQuery({ queryKey: keys.workspaces, queryFn: repo.workspaces, refetchInterval: refreshEvery });

export const useEmployees = () => useQuery({ queryKey: keys.employees, queryFn: repo.employees });

export const useEmployee = (id: string) => useQuery({ queryKey: keys.employee(id), queryFn: () => repo.employee(id) });

/** The shelf of employees changes with a release, not during a session: kept for an hour. */
export const useCatalog = (enabled = true) =>
  useQuery({ queryKey: keys.catalog, queryFn: repo.catalog, staleTime: 60 * 60_000, gcTime: 2 * 60 * 60_000, enabled });

/** Requests for new employees; the Talkos team moves them along, so they refresh like the lists. */
export const useEmployeeRequests = () =>
  useQuery({ queryKey: keys.employeeRequests, queryFn: repo.employeeRequests, refetchInterval: refreshEvery });

/** Everything that needs the person: highest priority first, then oldest. */
export const useInbox = (f: repo.ItemFilter = {}) =>
  useInfiniteQuery({
    queryKey: keys.inbox(f),
    queryFn: ({ pageParam }) => repo.inbox({ ...f, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    refetchInterval: refreshEvery,
  });

/** Every item, newest first. */
export const useItems = (f: repo.ItemFilter = {}, enabled = true) =>
  useInfiniteQuery({
    queryKey: keys.items(f),
    queryFn: ({ pageParam }) => repo.allItems({ ...f, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    enabled,
  });

export const useItem = (id: string) =>
  useQuery({
    queryKey: keys.item(id),
    queryFn: () => repo.item(id),
    // A task in progress gets checked on now and then.
    refetchInterval: (q) => (q.state.data && isWorking(q.state.data) ? pollEvery : false),
  });

export const useItemEvents = (id: string) => useQuery({ queryKey: keys.events(id), queryFn: () => repo.itemEvents(id) });

/** An item that came from this one (a task, or a task's result), with what came of it in turn. */
export interface RelatedItem {
  item: Item;
  results: Item[];
}

/** Tasks this item was handed off as (with their results), or a task's results. */
export const useRelated = (id: string) =>
  useQuery({
    queryKey: keys.related(id),
    queryFn: async (): Promise<RelatedItem[]> => {
      const items = await repo.related(id);
      return Promise.all(
        items.map(async (item) => ({ item, results: item.type === 'task' ? await repo.related(item.id).catch(() => []) : [] })),
      );
    },
    refetchInterval: (q) => (q.state.data?.some((r) => isWorking(r.item)) ? pollEvery : false),
  });

export const useHistory = (workspaceId?: string) =>
  useInfiniteQuery({
    queryKey: keys.history(workspaceId),
    queryFn: ({ pageParam }) => repo.history({ workspaceId, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  });

/** After anything changes an item, every list that may show it refreshes. */
function useRefreshAfterChange() {
  const qc = useQueryClient();
  return (changed?: Item) => {
    if (changed) qc.setQueryData(keys.item(changed.id), changed);
    return Promise.all([
      qc.invalidateQueries({ queryKey: ['items'] }),
      qc.invalidateQueries({ queryKey: ['item'] }),
      qc.invalidateQueries({ queryKey: keys.workspaces }),
      qc.invalidateQueries({ queryKey: keys.employees }),
      qc.invalidateQueries({ queryKey: ['history'] }),
    ]);
  };
}

export interface DecideArgs {
  item: Item;
  kind: DecisionKind;
  comment?: string;
  payload?: Record<string, unknown>;
}

export function useDecide() {
  const refresh = useRefreshAfterChange();
  return useMutation({
    mutationFn: (a: DecideArgs) => repo.decide(a.item, a.kind, { comment: a.comment, payload: a.payload }),
    onSuccess: (item) => refresh(item),
    // Someone else changed it: show the latest version instead of deciding blind.
    onError: (e) => {
      if (e instanceof AppError && (e.kind === 'stale' || e.kind === 'conflict')) void refresh();
    },
  });
}

/** Marks reports seen. Lists carry no version tag, so each is read first. Returns how many were. */
export function useMarkSeen() {
  const refresh = useRefreshAfterChange();
  return useMutation({
    mutationFn: async (items: Item[]) => {
      let done = 0;
      for (const it of items) {
        try {
          await repo.decide(await repo.item(it.id), 'approve');
          done++;
        } catch (e) {
          if (done === 0) throw e;
          break;
        }
      }
      return done;
    },
    onSettled: () => refresh(),
  });
}

export function useHandOff() {
  const refresh = useRefreshAfterChange();
  return useMutation({
    mutationFn: (a: { itemId: string; employeeId: string; note?: string }) => repo.handOff(a.itemId, a.employeeId, a.note),
    onSettled: () => refresh(),
  });
}

export function useReportDone() {
  const refresh = useRefreshAfterChange();
  return useMutation({
    mutationFn: (a: { itemId: string; link?: string; note?: string }) => repo.reportDone(a.itemId, a.link, a.note),
    onSuccess: (item) => refresh(item),
  });
}

/** Turns one kind of an employee's work automatic or back under review. The answer is the employee as
 * it is now, so their page and the team show it at once, and both refresh behind it. */
export function useSetAutonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { employeeId: string; kind: AutonomyKind; automatic: boolean }) =>
      repo.setAutonomy(a.employeeId, a.kind, a.automatic),
    onSuccess: (e) => {
      qc.setQueryData(keys.employee(e.id), e);
      qc.setQueryData<Employee[]>(keys.employees, (all) => all?.map((x) => (x.id === e.id ? e : x)));
      // `employees` is a prefix of `employee(id)`, so this refreshes the list and the single one.
      return qc.invalidateQueries({ queryKey: keys.employees });
    },
  });
}

/** Saves what the employee knows about the business; their page and the team show it at once. */
export function useSaveProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { employeeId: string; profile: EmployeeProfile }) => repo.saveProfile(a.employeeId, a.profile),
    onSuccess: (e) => {
      qc.setQueryData(keys.employee(e.id), e);
      qc.setQueryData<Employee[]>(keys.employees, (all) => all?.map((x) => (x.id === e.id ? e : x)));
      return qc.invalidateQueries({ queryKey: keys.employees });
    },
  });
}

/** Asks the Talkos team for a new employee; the team page lists it at once. */
export function useRequestEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: EmployeeRequestInput) => repo.requestEmployee(r),
    onSuccess: (r) => {
      qc.setQueryData<EmployeeRequest[]>(keys.employeeRequests, (all) => (all ? [r, ...all.filter((x) => x.id !== r.id)] : all));
      return qc.invalidateQueries({ queryKey: keys.employeeRequests });
    },
  });
}
